/**
 * Bulk tagging for both inboxes (#646).
 *
 * The bulk bar's `tag` key opens this panel over the list. One request per
 * submit — `POST /api/mail/tags` with the selection as `ids` and
 * `expand_conversations: true`, because a list row IS a conversation and
 * tagging it must tag every message the row stands for.
 *
 * The result is read, not assumed: `failed` lists the selected ids the server
 * would not tag (not reachable in this lane), and those alone stay selected so
 * the user can see which ones did not take — the same contract as the unified
 * lane's bulk Trash (`confirmBulkTrash`).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

import MailTagPicker, { tagErrorMessage } from './MailTagPicker';
import { useTagMail } from '../../hooks/useMailState';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * Toast a tag result and narrow the selection to what failed.
 * Exported so the thread view reports the same outcomes in the same words —
 * there `noun` is `'message'` and there is no selection to narrow.
 */
export function reportTagResult(data, { selection = null, verb = 'Tagged', noun = 'conversation' } = {}) {
    const failed = data?.failed || [];
    const requested = data?.requested ?? 0;
    const changed = (data?.added || 0) + (data?.removed || 0);
    if (failed.length && failed.length >= requested) {
        toast.error(requested === 1
            ? `This ${noun} could not be tagged`
            : `None of the selected ${noun}s could be tagged`);
        return 'error';
    }
    if (failed.length) {
        if (selection) {
            selection.clear();
            failed.forEach((f) => selection.toggleOne(f.comm_id, true));
        }
        toast.warning(
            `${verb}: ${plural(requested - failed.length, noun)}, `
            + `${failed.length} could not be tagged${selection ? ' — they remain selected' : ''}`,
        );
        return 'partial';
    }
    selection?.clear();
    if (!changed) {
        toast.warning('Nothing changed — the tags were already set');
        return 'noop';
    }
    toast.success(verb);
    return 'ok';
}

export function useBulkTagPanel({ inboxId = null, selection, entries = [] }) {
    const [open, setOpen] = useState(false);
    const tagMail = useTagMail();
    const { selectedIds, buildTarget } = selection;

    // The selection's own tags — what "remove" can meaningfully offer.
    const removable = useMemo(() => {
        const seen = new Map();
        entries
            .filter((e) => selectedIds.has(e.comm_id))
            .forEach((e) => (e.tags || []).forEach((t) => seen.set(t.tag_id, t)));
        return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [entries, selectedIds]);

    const close = useCallback(() => setOpen(false), []);
    const openPanel = useCallback(() => setOpen(true), []);

    const apply = useCallback(
        async ({ add = [], remove = [] }) => {
            if (!add.length && !remove.length) return;
            try {
                const res = await tagMail.mutateAsync({
                    target: buildTarget(inboxId),
                    add,
                    remove,
                    expand_conversations: true,
                });
                reportTagResult(res?.data, {
                    selection,
                    verb: add.length ? 'Tagged' : 'Tag removed',
                });
                setOpen(false);
            } catch (err) {
                toast.error(tagErrorMessage(err));
            }
        },
        [tagMail, buildTarget, inboxId, selection],
    );

    // Nothing selected (the selection was cleared under an open panel) → nothing to tag.
    const panel = open && selection.selectedCount > 0 ? (
        <div className="absolute right-4 top-11 z-40" data-testid="mail-bulk-tag-panel">
            <MailTagPicker
                heading={`Tag ${plural(selection.selectedCount, 'conversation')}`}
                removable={removable}
                onChange={apply}
                onClose={close}
                testId="mail-bulk-tag-picker"
            />
        </div>
    ) : null;

    return { openPanel, panel };
}
